import { useTranslation } from "react-i18next";
import React from 'react';
import { connect } from 'react-redux'; // Cache bust: 2025-09-10-15:40

const PropertyValue = ({
  propertyMeta,
  value,
  profile
}) => {
  const {
    t
  } = useTranslation();

  // Handle null/undefined value
  if (!value) {
    return <span className="text-muted">{t("No data")}</span>;
  } // Generic guard: format { parameters: {...} } into readable strings to avoid React child errors


  const renderParametersObject = params => {
    if (!params || typeof params !== 'object') return null; // Common parameter keys mapping

    const keys = Object.keys(params).sort();
    const parts = keys.map(k => params[k]?.value).filter(Boolean);
    return parts.join(', ');
  }; // Handle different property types


  if (propertyMeta.name === 'phoneNumbers') {
    let phoneNumber, type;

    if (value && value.parameters) {
      // Handle structured phone data
      phoneNumber = value.parameters[1]?.value || '';
      type = value.parameters[3]?.value || 'mobile';
    } else if (value.value && typeof value.value === 'string') {
      // Handle phone number stored as JSON string
      try {
        const phoneData = JSON.parse(value.value);
        phoneNumber = phoneData['1']?.value || value.value;
        type = phoneData['3']?.value || 'mobile';
      } catch (error) {
        // Fallback to treating as simple string
        phoneNumber = value.value;
        type = 'mobile';
      }
    } else {
      // Fallback
      phoneNumber = value.value || '';
      type = 'mobile';
    } // Format phone number with country code


    const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    return <div>
        <span>{formattedPhone}</span>
        <small className="text-muted d-block">({type})</small>
      </div>;
  }

  if (propertyMeta.name === 'emailAddresses' && value && (value.parameters || propertyMeta.parameters)) {
    const params = value.parameters || propertyMeta.parameters;
    const email = params[1]?.value || '';
    const type = params[2]?.value || 'work';
    return <div>
        <span>{email}</span>
        <small className="text-muted d-block">({type})</small>
      </div>;
  }

  if (propertyMeta.name === 'postalAddresses' && value && (value.parameters || propertyMeta.parameters)) {
    const params = value.parameters || propertyMeta.parameters;
    const street = params[1]?.value || '';
    const city = params[2]?.value || '';
    const state = params[3]?.value || '';
    const postalCode = params[4]?.value || '';
    const country = params[5]?.value || '';
    const type = params[6]?.value || 'home';
    return <div>
        <div>{street}</div>
        <div>{city}, {state} {postalCode}</div>
        <div>{country}</div>
        <small className="text-muted">({type})</small>
      </div>;
  }

  if (propertyMeta.name === 'urlAddresses' && value && (value.parameters || propertyMeta.parameters)) {
    const params = value.parameters || propertyMeta.parameters;
    const url = params[1]?.value || '';
    const type = params[2]?.value || 'website';
    return <div>
        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
        <small className="text-muted d-block">({type})</small>
      </div>;
  }

  if (propertyMeta.name === 'thumbnailImage') {
    // Handle both object format {value: "url..."} and direct string format "url..."
    const imageData = value.value || value;

    if (imageData && typeof imageData === 'string' && imageData.trim()) {
      // Check if it's a local file path (from mobile app)
      if (imageData.startsWith('file://') || imageData.startsWith('/Users/') || imageData.startsWith('/var/')) {
        // It's a local file path - show placeholder instead
        return <div>
            <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6c757d',
            fontSize: '12px',
            textAlign: 'center'
          }}>
              <span role="img" aria-label="camera">📷</span>
            </div>
            <small className="text-muted d-block mt-1">{t("Photo from mobile app")}</small>
          </div>;
      } else if (imageData.startsWith('data:')) {
        // It's already a data URL
        return <div>
            <img src={imageData} alt={t("Profile")} style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            objectFit: 'cover'
          }} />
          </div>;
      } else if (imageData.startsWith('/api/uploads/') || imageData.startsWith('http')) {
        // It's a server URL
        return <div>
            <img src={imageData} alt={t("Profile")} style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            objectFit: 'cover'
          }} />
          </div>;
      } else {
        // It's base64 data - add the data URL prefix
        return <div>
            <img src={`data:image/jpeg;base64,${imageData}`} alt={t("Profile")} style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            objectFit: 'cover'
          }} />
          </div>;
      }
    }

    const getInitials = () => {
      if (!profile || !profile.properties) return '?';
      const firstNameProp = profile.properties.find(p => p.name === 'givenName');
      const lastNameProp = profile.properties.find(p => p.name === 'familyName');
      const first = firstNameProp ? (firstNameProp.displayValue || firstNameProp.value || '').charAt(0) : '';
      const last = lastNameProp ? (lastNameProp.displayValue || lastNameProp.value || '').charAt(0) : '';
      return (first + last).toUpperCase() || '?';
    };

    return <div style={{
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: '#00A956',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '16px',
      textTransform: 'uppercase'
    }}>
        {getInitials()}
      </div>;
  }

  if (propertyMeta.name === 'howisaymyname' && value.value) {
    return <div>
        <audio controls>
          <source src={value.value} type="audio/wav" />{t("Your browser does not support the audio element.")}</audio>
      </div>;
  }

  if (propertyMeta.name === 'onmymind') {
    // Handle onmymind property - extract the actual text value
    let textValue = '';

    if (value && typeof value === 'object') {
      // Check if it has parameters structure
      if (value.parameters && value.parameters['1'] && value.parameters['1'].value) {
        textValue = value.parameters['1'].value;
      } else if (value.value && typeof value.value === 'string') {
        textValue = value.value;
      } else if (value.value && typeof value.value === 'object' && value.value.value) {
        textValue = value.value.value;
      }
    } else if (typeof value === 'string') {
      textValue = value;
    }

    return <div>
        <span className="font-italic">{textValue || 'No thoughts shared'}</span>
      </div>;
  } // Default display for simple values


  if (value && typeof value === 'object' && value.value !== undefined && typeof value.value !== 'object') {
    return <span>{String(value.value)}</span>;
  } // If value.value is an object with parameters, format it


  if (value && typeof value === 'object' && value.value && typeof value.value === 'object' && value.value.parameters) {
    const formatted = renderParametersObject(value.value.parameters);
    return <span>{formatted || '—'}</span>;
  } // Handle case where value is a string directly


  if (typeof value === 'string') {
    return <span>{value}</span>;
  } // Handle case where value is a number


  if (typeof value === 'number') {
    return <span>{String(value)}</span>;
  } // Handle case where value is an object but we need to extract a display value


  if (value && typeof value === 'object') {
    if (value.parameters) {
      const formatted = renderParametersObject(value.parameters);
      return <span>{formatted || '—'}</span>;
    } // Try to find a displayable value


    const displayValue = value.value || value.name || value.showName || JSON.stringify(value);
    return <span>{String(displayValue)}</span>;
  } // Fallback


  return <span>—</span>;
};

const mapStateToProps = state => ({
  profile: state.auth.currentUser ? state.auth.currentUser.profile : undefined
});

export default connect(mapStateToProps)(PropertyValue);